const results = [
  {
    icon: 'ri-eye-line',
    title: 'Clear Visibility',
    description: 'You understand your nervous system\'s background patterns and hidden triggers clearly.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Fewer Crashes',
    description: 'You catch early warning signs and navigate disruptions safely without collapsing.',
  },
  {
    icon: 'ri-scales-3-line',
    title: 'Guilt-Free Decisions',
    description: 'You budget your daily capacity and build structures that ease hard choices.',
  },
  {
    icon: 'ri-leaf-line',
    title: 'Lasting Confidence',
    description: 'You build a resilient, predictable life that fully works in harmony with your body.',
  },
];

export default function ProblemSolution() {
  return (
    <section className="relative overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          alt="Soft airy botanical background"
          className="w-full h-full object-cover"
          src="https://readdy.ai/api/search-image?query=Very%20soft%20pale%20white%20and%20light%20gray%20background%20with%20delicate%20airy%20eucalyptus%20leaves%20and%20fern%20branches%2C%20minimalist%20botanical%20composition%2C%20subtle%20watercolor%20style%2C%20gentle%20diffused%20light%2C%20calming%20wellness%20aesthetic%2C%20muted%20sage%20and%20soft%20silver%20tones%2C%20editorial%20photography%2C%20serene%20peaceful%20atmosphere&width=1600&height=900&seq=course-problem-01&orientation=landscape"
        />
        <div className="absolute inset-0 bg-white/75" />
      </div>

      <div className="relative z-10 section-padding max-w-6xl mx-auto">
        {/* Problem / Solution */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {/* Problem */}
          <div className="bg-[#6B8FA3]/90 backdrop-blur-sm border border-[#5A7F94]/40 rounded-xl p-7 shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
            <div className="flex items-center gap-2 mb-5">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-white/15">
                <i className="ri-error-warning-line text-white" />
              </span>
              <h2 className="font-heading text-xl font-bold text-white">The Problem</h2>
            </div>
            <p className="text-sm text-white/80 mb-5 leading-relaxed">
              It&apos;s a system problem. The Boom and Bust cycle keeps you trapped:
            </p>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="px-3 py-1.5 bg-white/15 rounded-md border border-white/20 text-white/90 shadow-sm">Push on a good day</span>
              <span className="text-white/40">
                <i className="ri-arrow-right-line" />
              </span>
              <span className="px-3 py-1.5 bg-white/15 rounded-md border border-white/20 text-white/90 shadow-sm">Overdo it</span>
              <span className="text-white/40">
                <i className="ri-arrow-right-line" />
              </span>
              <span className="px-3 py-1.5 bg-white/15 rounded-md border border-white/20 text-white/90 shadow-sm">Crash</span>
              <span className="text-white/40">
                <i className="ri-arrow-right-line" />
              </span>
              <span className="px-3 py-1.5 bg-white/15 rounded-md border border-white/20 text-white/90 shadow-sm">Rest</span>
              <span className="text-white/40">
                <i className="ri-arrow-right-line" />
              </span>
              <span className="px-3 py-1.5 bg-white/20 rounded-md border border-white/30 text-white font-medium shadow-sm">Repeat</span>
            </div>
          </div>

          {/* Solution */}
          <div className="bg-[#6B8FA3]/90 backdrop-blur-sm border border-[#5A7F94]/40 rounded-xl p-7 shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
            <div className="flex items-center gap-2 mb-5">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-white/15">
                <i className="ri-check-double-line text-white" />
              </span>
              <h2 className="font-heading text-xl font-bold text-white">The Solution</h2>
            </div>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-sm text-white/80">
                <span className="mt-0.5 w-5 h-5 flex items-center justify-center shrink-0">
                  <i className="ri-eye-line text-white/80" />
                </span>
                <span>See where your energy truly goes</span>
              </li>
              <li className="flex items-start gap-3 text-sm text-white/80">
                <span className="mt-0.5 w-5 h-5 flex items-center justify-center shrink-0">
                  <i className="ri-drop-line text-white/80" />
                </span>
                <span>Stop hidden energy drain</span>
              </li>
              <li className="flex items-start gap-3 text-sm text-white/80">
                <span className="mt-0.5 w-5 h-5 flex items-center justify-center shrink-0">
                  <i className="ri-shield-check-line text-white/80" />
                </span>
                <span>Protect your baseline with a personal Safety Net</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Results */}
        <div>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-primary-800 text-center mb-3">
            Imagine This
          </h2>
          <p className="text-sm text-foreground-500 text-center mb-10">Results for life, not just for today</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {results.map((r) => (
              <div
                key={r.title}
                className="bg-[#6B8FA3]/90 backdrop-blur-sm border border-[#5A7F94]/40 rounded-xl p-6 shadow-lg hover:shadow-xl hover:border-white/30 hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/15 mb-4">
                  <i className={`${r.icon} text-lg text-white`} />
                </div>
                <h3 className="font-heading text-base font-semibold text-white mb-2">
                  {r.title}
                </h3>
                <p className="text-sm text-white/70 leading-relaxed">
                  {r.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}